import React, { useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { eventApi } from '../../../modules/services/eventService';
import translate from '../../../i18n/translate';

interface Props {
  eventId: number;
}

const EventInfoTooltip: React.FC<Props> = ({ eventId }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [triggerFetch, { data, isFetching }] =
    eventApi.useLazyFetchEventsQuery();

  const event = data?.list?.[0];

  const handleOpen = () => {
    setOpen(true);
    if (!data && !isFetching) {
      triggerFetch({ type: 'all', eventId, skip: 0, take: 1, filter: '' });
    }
  };

  const handleClick = () => {
    setOpen(false);
    navigate(`/events/all?eventId=${eventId}`);
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 260 }}>
      {isFetching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          <CircularProgress size={20} color="warning" />
        </Box>
      )}
      {!isFetching && !event && (
        <Typography variant="body2">{translate('marketingPersonsSearchNoResult')}</Typography>
      )}
      {!isFetching && event && (
        <>
          <Typography variant="body2" fontWeight="bold" color="warning.main" sx={{ mb: 0.5 }}>
            {event.name} (ID: {event.id})
          </Typography>
          <Typography variant="caption">
            {translate('gridEventsTotalTicketsPurchasedInCredit')}: ₪{Number(event.totalTicketsPurchasedInCredit ?? 0).toLocaleString()}
          </Typography>
          <Typography variant="caption">
            {translate('gridEventsTotalCommisionProfit')}: ₪{Number(event.totalCommissionProfit ?? 0).toLocaleString()}
          </Typography>
          <Typography variant="caption">
            {translate('gridEventsTotalTicketsSold')}: {event.totalTicketsSold ?? 0}
          </Typography>
          <Typography variant="caption">
            {translate('gridEventsCanceledMoney')}: ₪{Number(event.canceledMoney ?? 0).toLocaleString()}
          </Typography>
          <Typography variant="caption">
            {translate('gridEventsCanceledCount')}: {event.canceledCount ?? 0}
          </Typography>
          <Typography variant="caption">
            {translate('gridEventsReturnedMoney')}: ₪{Number(event.returnedMoney ?? 0).toLocaleString()}
          </Typography>
          <Typography variant="caption">
            {translate('gridEventsReturnedCount')}: {event.returnedCount ?? 0}
          </Typography>
        </>
      )}
    </Box>
  );

  return (
    <Tooltip
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      title={content}
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: '#333',
            color: '#fff',
            maxWidth: 360,
            p: 1.5,
            '& .MuiTypography-root': { color: '#fff' },
          },
        },
        arrow: { sx: { color: '#333' } },
      }}
    >
      <IconButton size="small" color="warning" onClick={handleClick} sx={{ p: 0.25 }}>
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

export default EventInfoTooltip;
